import { cloneElement, FC, ReactElement, ReactNode, useState } from 'react'
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  safePolygon,
  type Placement,
} from '@floating-ui/react'

type FloatingHoverCardProps = {
  portalId?: string

  placement?: Placement
  offsetPx?: number
  shiftPadding?: number
  openDelayMs?: number
  closeDelayMs?: number
  safePolygonBuffer?: number

  // controlled mode
  open?: boolean
  onOpenChange?: (open: boolean) => void

  trigger: ReactElement
  className?: string

  children: ReactNode
  disabled?: boolean
}

export const FloatingHoverCard: FC<FloatingHoverCardProps> = ({
  portalId,
  placement = 'bottom-start',
  offsetPx = 8,
  shiftPadding = 8,
  openDelayMs = 1000,
  closeDelayMs = 100,
  safePolygonBuffer = 2,
  open: openProp,
  onOpenChange,
  trigger,
  className,
  children,
  disabled,
}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const open = openProp ?? uncontrolledOpen
  const setOpen = onOpenChange ?? setUncontrolledOpen

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(offsetPx), flip(), shift({ padding: shiftPadding })],
    whileElementsMounted: autoUpdate,
  })

  const hover = useHover(context, {
    enabled: !disabled,
    delay: { open: openDelayMs, close: closeDelayMs },
    handleClose: safePolygon({ buffer: safePolygonBuffer }),
  })

  const dismiss = useDismiss(context, { escapeKey: true })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
  ])

  return (
    <>
      {cloneElement(trigger, {
        ref: refs.setReference,
        ...getReferenceProps(),
      })}

      {!disabled && open && (
        <FloatingPortal id={portalId}>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={className}
            {...getFloatingProps()}
          >
            {children}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
